import React, { useState, useEffect } from 'react';
import ContentBox from '../components/ContentBox';
import ColorPicker from '../components/ColorPicker';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Each entry can be a plain string (uses default direction 2)
// or an object { name, direction } for per-furni overrides.
const FURNI_GROUPS = {
    'Classic Rares': [
        'rare_dragonlamp',
        'rare_fountain',
        'rare_icecream',
        'rare_fan',
        { name: 'rare_elephant_statue', direction: 6 },
        'scifirocket',
        'scifiport',
        'scifidoor',
        'pillow',
        'sleepingbag',
        'rare_beehive_bulb',
        'marquee',
        'pillar',
        'wooden_screen',
        'rare_parasol',
        'rubberchair',
        'barrier',
        'traffic_light',
    ],
    'Plasto': [
        'table_plasto_bigsquare',
        'table_plasto_4leg',
        'table_plasto_round',
        'table_plasto_square',
        'chair_plasto',
        'chair_plasty',
    ],
    'Iced': [
        'couch_norja2',
        'shelves_norja2',
        'chair_norja2',
        'divider_nor12',
        'divider_nor22',
        'soft_sofa_norja2',
        'soft_sofachair_norja2',
    ],
    'Area': [
        { name: 'sofachair_silo2', direction: 1 },
        { name: 'sofa_silo2', direction: 1 },
        { name: 'table_silo_small2'},
        { name: 'table_silo_med2' },
        { name: 'divider_silo12', direction: 0 },
    ],
    'Gothic': [
        { name: 'gothic_chair', direction: 6 },
        { name: 'gothic_sofa', direction: 6 },
        { name: 'gothic_stool', direction: 6 }
    ],
    'Trophies': [
        'prizetrophy',
        'hween_trophy',
    ]
};

const DEFAULT_DIRECTION = 2;

// Normalise a group entry into { name, direction }
const normaliseFurni = (entry) =>
    typeof entry === 'string'
        ? { name: entry, direction: DEFAULT_DIRECTION }
        : { name: entry.name, direction: entry.direction ?? DEFAULT_DIRECTION };

const GROUP_NAMES = Object.keys(FURNI_GROUPS);

function FurniColor() {
    const [color, setColor] = useState('#FFFFFF');
    const [selectedGroup, setSelectedGroup] = useState(GROUP_NAMES[0]);
    const [renderedImages, setRenderedImages] = useState({});
    const [initialLoading, setInitialLoading] = useState(true);
    const [applying, setApplying] = useState(false);
    const [loadingMap, setLoadingMap] = useState({});
    const [actionMap, setActionMap] = useState({});
    const API_BASE = 'https://furni.franklyorigins.net';

    const furnitureEntries = FURNI_GROUPS[selectedGroup].map(normaliseFurni);

    useEffect(() => {
        LoadGroupFurniture(selectedGroup, color);
        return () => {
            Object.values(renderedImages).forEach((url) => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, []);

    useEffect(() => {
        const currentEntries = FURNI_GROUPS[selectedGroup].map(normaliseFurni);
        const missing = currentEntries.filter(({ name }) => !renderedImages[name]);

        if (missing.length === 0) return;

        const loadMissing = async () => {
            setInitialLoading(true);
            const nextLoadingMap = {};
            for (const { name } of missing) nextLoadingMap[name] = true;
            setLoadingMap((prev) => ({ ...prev, ...nextLoadingMap }));

            try {
                await RenderFurnitureWithColor(missing, color);
            } finally {
                setLoadingMap((prev) => {
                    const next = { ...prev };
                    for (const { name } of missing) next[name] = false;
                    return next;
                });
                setInitialLoading(false);
            }
        };

        loadMissing();
    }, [selectedGroup]);

    const LoadGroupFurniture = async (groupName, hexColor) => {
        const entries = FURNI_GROUPS[groupName].map(normaliseFurni);
        try {
            setInitialLoading(true);
            const nextLoadingMap = {};
            for (const { name } of entries) nextLoadingMap[name] = true;
            setLoadingMap(nextLoadingMap);
            await RenderFurnitureWithColor(entries, hexColor);
        } catch (error) {
            console.error('Error loading furniture:', error);
        } finally {
            setInitialLoading(false);
            setLoadingMap((prev) => {
                const next = { ...prev };
                for (const { name } of entries) next[name] = false;
                return next;
            });
        }
    };

    // entries: Array of { name, direction }
    const RenderFurnitureWithColor = async (entries, hexColor) => {
        const results = await Promise.allSettled(
            entries.map(async ({ name, direction }) => {
                const queryParams = new URLSearchParams({
                    furni: name,
                    direction,
                    state: 1,
                    frame: 0,
                    hex: hexColor,
                });

                const response = await fetch(`${API_BASE}/api/furniture/render?${queryParams}`);
                if (!response.ok) throw new Error(`Failed to render ${name}`);

                const blob = await response.blob();
                const newUrl = URL.createObjectURL(blob);

                return { name, newUrl };
            })
        );

        setRenderedImages((prev) => {
            const next = { ...prev };

            for (const result of results) {
                if (result.status === 'fulfilled') {
                    const { name, newUrl } = result.value;
                    const oldUrl = next[name];
                    next[name] = newUrl;
                    if (oldUrl) URL.revokeObjectURL(oldUrl);
                } else {
                    console.error(result.reason);
                }
            }

            return next;
        });
    };

    const HandleColorChange = (e) => {
        setColor(e.target.value);
    };

    const HandleApplyColor = async () => {
        setApplying(true);

        const nextLoadingMap = {};
        for (const { name } of furnitureEntries) nextLoadingMap[name] = true;
        setLoadingMap((prev) => ({ ...prev, ...nextLoadingMap }));

        try {
            await RenderFurnitureWithColor(furnitureEntries, color);
        } finally {
            setLoadingMap((prev) => {
                const next = { ...prev };
                for (const { name } of furnitureEntries) next[name] = false;
                return next;
            });
            setApplying(false);
        }
    };

    const WithAction = async (furniName, actionName, fn) => {
        const key = `${furniName}:${actionName}`;
        setActionMap((prev) => ({ ...prev, [key]: true }));
        try {
            await fn();
        } finally {
            setActionMap((prev) => ({ ...prev, [key]: false }));
        }
    };

    const FetchImageBlobFor = async (furniName, direction) => {
        const queryParams = new URLSearchParams({
            furni: furniName,
            direction,
            state: 1,
            frame: 0,
            hex: color,
        });

        const response = await fetch(`${API_BASE}/api/furniture/render?${queryParams}`);
        if (!response.ok) throw new Error(`Failed to fetch ${furniName}`);
        return await response.blob();
    };

    const CopyFurniToClipboard = async (furniName, direction) => {
        const blob = await FetchImageBlobFor(furniName, direction);
        if (!navigator.clipboard || !window.ClipboardItem) throw new Error('Clipboard image copy not supported in this browser');
        await navigator.clipboard.write([new ClipboardItem({ [blob.type || 'image/png']: blob })]);
        toast.success(`${furniName} copied to clipboard`);
    };

    const SaveFurniAsPng = async (furniName, direction) => {
        const blob = await FetchImageBlobFor(furniName, direction);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${furniName}_${color.replace('#', '').toUpperCase()}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`${furniName} saved as PNG`);
    };

    const groupIsLoading = initialLoading && furnitureEntries.some(({ name }) => !renderedImages[name]);

    return (
        <div className="w-full min-h-screen p-4 lg:p-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-3">
                        <ContentBox theme="default" tint="maroon" header="Colour Wheel">
                            <div className="flex flex-col items-center space-y-4">
                                <ColorPicker
                                    value={color}
                                    onChange={HandleColorChange}
                                    className="w-full h-48 border-2 border-gray-400 rounded cursor-pointer"
                                />

                                <div className="w-full text-center">
                                    <p className="text-xs text-gray-600 mb-1">Selected Colour</p>
                                    <p className="text-lg font-bold font-mono text-gray-900">{color.toUpperCase()}</p>
                                </div>

                                <button
                                    onClick={HandleApplyColor}
                                    disabled={groupIsLoading || applying}
                                    className="w-full px-6 py-3 bg-maroon text-white rounded hover:opacity-90 transition-opacity font-['Jersey_10'] uppercase text-sm tracking-wide disabled:opacity-50"
                                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                >
                                    {groupIsLoading ? 'Loading...' : applying ? 'Applying...' : 'Apply'}
                                </button>
                            </div>
                        </ContentBox>
                    </div>

                    <div className="lg:col-span-9">
                        <ContentBox theme="default" tint="lightskyblue" header="Habbo Furni Recolourer">
                            <div className="flex items-center gap-3 mb-4">
                                <label className="text-xs uppercase tracking-wide font-bold text-gray-700 font-['Jersey_10'] whitespace-nowrap">
                                    Furni Group
                                </label>
                                <select
                                    value={selectedGroup}
                                    onChange={(e) => setSelectedGroup(e.target.value)}
                                    disabled={applying}
                                    className="flex-1 px-3 py-2 border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:border-maroon disabled:opacity-50 font-['Jersey_10'] uppercase tracking-wide"
                                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.1)' }}
                                >
                                    {GROUP_NAMES.map((name) => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            </div>

                            {groupIsLoading ? (
                                <div className="text-center py-12">
                                    <p className="text-gray-600 text-lg">Loading...</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {furnitureEntries.map(({ name, direction }) => {
                                        const isLoading = !!loadingMap[name];
                                        const hasImage = !!renderedImages[name];
                                        const copyBusy = !!actionMap[`${name}:copy`];
                                        const saveBusy = !!actionMap[`${name}:save`];
                                        const buttonsDisabled = isLoading || !hasImage || copyBusy || saveBusy;

                                        return (
                                            <div key={name} className="bg-gray-200/50 rounded p-4 text-center">
                                                <div className="relative bg-black/80 p-4 rounded mb-3 flex items-center justify-center h-40 overflow-hidden">
                                                    {hasImage ? (
                                                        <img
                                                            src={renderedImages[name]}
                                                            alt={name}
                                                            className="max-w-full"
                                                            style={{ imageRendering: 'pixelated' }}
                                                        />
                                                    ) : (
                                                        <span className="text-gray-500 text-sm">Loading...</span>
                                                    )}

                                                    {isLoading && (
                                                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                            <div className="w-10 h-10 border-4 border-white/40 border-t-white rounded-full animate-spin" />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                        onClick={() =>
                                                            WithAction(name, 'copy', async () => {
                                                                try {
                                                                    await CopyFurniToClipboard(name, direction);
                                                                } catch (error) {
                                                                    console.error(error);
                                                                }
                                                            })
                                                        }
                                                        disabled={buttonsDisabled}
                                                        className="w-full px-3 py-2 bg-maroon text-white rounded hover:opacity-90 transition-opacity font-['Jersey_10'] uppercase text-sm tracking-wide disabled:opacity-50"
                                                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                                    >
                                                        {copyBusy ? 'Copying...' : 'Copy'}
                                                    </button>

                                                    <button
                                                        onClick={() =>
                                                            WithAction(name, 'save', async () => {
                                                                try {
                                                                    await SaveFurniAsPng(name, direction);
                                                                } catch (error) {
                                                                    console.error(error);
                                                                }
                                                            })
                                                        }
                                                        disabled={buttonsDisabled}
                                                        className="w-full px-3 py-2 bg-maroon text-white rounded hover:opacity-90 transition-opacity font-['Jersey_10'] uppercase text-sm tracking-wide disabled:opacity-50"
                                                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                                    >
                                                        {saveBusy ? 'Saving...' : 'Save'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </ContentBox>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FurniColor;